import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WalletService } from 'src/app/services/wallet/wallet.service';
import { ToastrService } from 'ngx-toastr';
import { DateTime } from 'luxon';
import { UserService } from 'src/app/services/user/user.service';
import { finalize } from 'rxjs';
import { Wallet } from 'src/app/model/Wallet';
import { AchService, PendingPaymentData } from 'src/app/services/ach/ach.service';
import { NLSTransaction } from 'src/app/model/NLSTransaction';

/** Provided data to make payment dialog */
export interface MakePaymentDialogData {
    transaction: NLSTransaction;
}

/** Response data from make payment dialog */
export interface MakePaymentDialogResponse {
    success: boolean;
    transaction?: NLSTransaction;
    paymentData?: {
        transactionId: number;
        loanNumber: string;
        walletId?: string;
        amount: number;
        paymentDate: Date | null;
    };
}

/**
 * Dialog component for making a payment for a transaction
 * Allows selecting payment method, amount, and payment date
 */
@Component({
    selector: 'app-make-payment-dialog',
    templateUrl: './make-payment-dialog.component.html',
    styleUrls: ['./make-payment-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MakePaymentDialogComponent implements OnInit {
    /** Available wallets for payment method selection */
    public wallets: Wallet[] = [];

    /** Selected wallet for payment */
    public selectedWallet: Wallet | null = null;

    /** Amount to pay */
    public amountToPay: number = 0;

    /** Payment date */
    public paymentDate: Date | null = null;

    /** Submission state flag */
    public isSubmitting: boolean = false;

    /** Today's date for date picker validation */
    public today: Date = DateTime.now().startOf('day').toJSDate();

    /** Full name for display */
    public fullName: string = '';

    /**
     * Default constructor to initialize the dialog component
     */
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: MakePaymentDialogData,
        public dialogRef: MatDialogRef<MakePaymentDialogComponent>,
        private _walletService: WalletService,
        private _achService: AchService,
        private _toastr: ToastrService,
        private _userService: UserService,
    ) { }

  // Derived, filtered list for the UI
  public get selectableWallets(): Wallet[] {
    return this.wallets.filter(w => !this.isPendingWallet(w));
  }

  private isPendingWallet(w: Wallet): boolean {
    const text = `${w?.nickname ?? ''} ${ (w as any)?.bankName ?? '' }`.toLowerCase();
    return text.includes('[p]');
  }

  async ngOnInit(): Promise<void> {
    const sub = this._walletService.wallets$.subscribe((wallets: Wallet[]) => {
      if (wallets && wallets.length > 0) {
        this.wallets = wallets;

        // preselect the first NON-pending wallet
        this.selectedWallet = this.selectableWallets[0] ?? null;
        sub.unsubscribe();
      }
    });

    // Fallback to current cache
    this.wallets = this._walletService.getWallets() || [];
    if (this.wallets.length > 0) {
      this.selectedWallet = this.selectableWallets[0] ?? null;
      // only unsubscribe if the observable already emitted above; harmless if already closed
      try { sub.unsubscribe(); } catch {}
    }

    this.fullName = `${this._userService.user?.firstName} ${this._userService.user?.lastName}`;

    /*
    if (this.data.transaction?.current_balance) {
      this.amountToPay = this.data.transaction.current_balance;
    }
    */
  }

    /**
     * Submit the payment
     */
    public onSubmitPayment(): void {
        try {
            if (!this.data.transaction) {
                throw new Error('No transaction data available');
            }

            this.isSubmitting = true;

            // Construct request body
            const paymentData: PendingPaymentData = {
                transactionId: this.data.transaction.transaction_id,
                loanNumber: this.data.transaction.loan_number,
                walletId: this.selectedWallet?.achwalletID,
                amount: this.amountToPay,
                paymentDate: this.paymentDate,
            };

            // Submit ach payment
            this._achService.submitAch$(paymentData)
            .pipe(finalize(() => setTimeout(() => this.isSubmitting = false, 1000)))
            .subscribe({
                next: (_: any) => {
                    this._toastr.success('Payment submitted successfully!');

                    // Close the dialog with success result
                    this.dialogRef.close({
                        success: true,
                        transaction: this.data.transaction,
                        paymentData: paymentData,
                    } as MakePaymentDialogResponse);
                },
                error: (err: any) => {
                    console.error("[MAKE-PAYMENT-DIALOG]: error submitting payment", err);
                    this._toastr.error('Payment submission failed');
                }
            });
        } catch (err) {
            console.error("[MAKE-PAYMENT-DIALOG]: exception submitting payment", err);
            this._toastr.error('Payment submission failed');
            this.isSubmitting = false;
        }
    }

    /**
     * Close the dialog without saving
     */
    public cancel(): void {
        this.dialogRef.close({ success: false } as MakePaymentDialogResponse);
    }
}
